import React from 'react';

import { useAppSelector } from '@/store';

import { linksByCampaign } from './link-attribution';
import { fetchTrackingLinks, type TrackingLink } from './tracking-links-api';

/**
 * Every tracking link of the active brand (`GET /creatives/tenant/{t}/tracking-links`, each with its asset and landing
 * page), keyed by `campaign` for attribution (`link-attribution.ts`). Shared by the Players page, Home and anything that
 * turns a stored campaign value into "came from this ad", so a page that shows several of them reads once: the answer
 * is kept per brand for a minute. A failed re-read keeps the last good answer; only a first read that fails is empty.
 */
export interface BrandLinks {
  links: TrackingLink[];
  byCampaign: ReadonlyMap<string, TrackingLink>;
  /** The brand's tenant id the links are for (undefined while no brand resolves). */
  tenantId?: number;
  /** True until the first read for this brand lands. */
  loading: boolean;
  error: string | null;
  /** Read again now (drops the cached answer). */
  reload: () => void;
}

const CACHE_MS = 60 * 1000;

interface CacheEntry {
  at: number;
  request: Promise<TrackingLink[]>;
}

const cache = new Map<number, CacheEntry>();
const NO_LINKS: TrackingLink[] = [];
const lastGood = new Map<number, TrackingLink[]>();

const readLinks = (tenantId: number, force: boolean): Promise<TrackingLink[]> => {
  const hit = cache.get(tenantId);
  if (!force && hit && Date.now() - hit.at < CACHE_MS) return hit.request;
  const request = fetchTrackingLinks(tenantId).then((res) => {
    if (res.ok) {
      lastGood.set(tenantId, res.data);
      return res.data;
    }
    cache.delete(tenantId);
    const kept = lastGood.get(tenantId);
    if (kept) return kept;
    throw new Error(res.error || `tracking links unavailable (${res.status})`);
  });
  cache.set(tenantId, { at: Date.now(), request });
  return request;
};

/** Forget the brand's cached links (e.g. right after a link was created or moved). */
export const invalidateBrandLinks = (tenantId: number): void => {
  cache.delete(tenantId);
};

/** The active brand's tenant id (the lagged displayBrand, like the creatives pages). */
const useTenantId = (): number | undefined => {
  const raw = useAppSelector((state) => Number(state.casinoInstance.displayBrand?.tenant_id));
  return Number.isFinite(raw) && raw > 0 ? raw : undefined;
};

export function useBrandLinks(): BrandLinks {
  const tenantId = useTenantId();
  const [tick, setTick] = React.useState(0);
  const forceRef = React.useRef(false);
  const [state, setState] = React.useState<{ for?: number; links: TrackingLink[]; loading: boolean; error: string | null }>(
    { links: [], loading: true, error: null },
  );
  const reqIdRef = React.useRef(0);

  // Syncs with the network: the brand's links (cached per tenant).
  React.useEffect(() => {
    const reqId = ++reqIdRef.current;
    if (!tenantId) {
      setState({ links: [], loading: false, error: null });
      return;
    }
    const force = forceRef.current;
    forceRef.current = false;
    setState((cur) => (cur.for === tenantId ? cur : { for: tenantId, links: lastGood.get(tenantId) ?? [], loading: true, error: null }));
    readLinks(tenantId, force)
      .then((links) => {
        if (reqId === reqIdRef.current) setState({ for: tenantId, links, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (reqId !== reqIdRef.current) return;
        setState({ for: tenantId, links: [], loading: false, error: err instanceof Error ? err.message : String(err) });
      });
  }, [tenantId, tick]);

  const reload = React.useCallback(() => {
    forceRef.current = true;
    setTick((n) => n + 1);
  }, []);
  // Until the effect has reset the state for a new brand, it still holds the previous brand's answer: never hand that
  // out as this brand's settled links (consumers would resolve or stamp the new brand against it).
  const mine = tenantId !== undefined && state.for === tenantId;
  const links = mine ? state.links : (tenantId !== undefined ? lastGood.get(tenantId) : undefined) ?? NO_LINKS;
  const byCampaign = React.useMemo(() => linksByCampaign(links), [links]);
  return {
    links,
    byCampaign,
    tenantId,
    loading: tenantId === undefined ? state.loading : !mine || state.loading,
    error: mine ? state.error : null,
    reload,
  };
}

export default useBrandLinks;
